import {useEffect,useState} from 'react';

type Priority='high'|'background';
type CacheEntry={endpoint:string;state:'idle'|'loading'|'ready'|'error';src:string|null;promise:Promise<void>|null;error:unknown|null;priority:Priority;listeners:Set<()=>void>};

const endpointOf=(value:any):string|null=>{
 const endpoint=value?.asset?.delivery?.endpoint??value?.delivery?.endpoint;
 return typeof endpoint==='string'&&/^\/api\/v1\/assets\/[0-9a-f-]{36}$/i.test(endpoint)?endpoint:null;
};
const entries=new Map<string,CacheEntry>();
let active=0;
export const MAX_CONCURRENT_IMAGE_FETCHES=2;

function entryFor(endpoint:string,priority:Priority):CacheEntry{
 const existing=entries.get(endpoint);
 if(existing){if(priority==='high')existing.priority='high';return existing;}
 const entry:CacheEntry={endpoint,state:'idle',src:null,promise:null,error:null,priority,listeners:new Set()};entries.set(endpoint,entry);return entry;
}
function schedule(){
 while(active<MAX_CONCURRENT_IMAGE_FETCHES){
  const next=[...entries.values()].find(entry=>entry.state==='idle'&&entry.priority==='high')??[...entries.values()].find(entry=>entry.state==='idle');
  if(!next)return;
  active++;next.state='loading';
  next.promise=(async()=>{
   try{
    const response=await fetch(next.endpoint,{credentials:'include',cache:'force-cache'});
    if(!response.ok)throw new Error(`IMAGE_${response.status}`);
    next.src=URL.createObjectURL(await response.blob());next.state='ready';
   }catch(error){next.error=error;next.state='error';}
   finally{for(const listener of next.listeners)listener();active--;schedule();}
  })();
 }
}

/**
 * Course images remain authenticated. Every endpoint is fetched once per
 * browser session and visible renderers use the very same Blob URL.
 */
export function preloadProtectedImages(values:any[],priority:Priority='background'){
 for(const endpoint of [...new Set(values.map(endpointOf).filter((value):value is string=>Boolean(value)))])entryFor(endpoint,priority);
 schedule();
}
export function protectedImageEndpoint(value:any){return endpointOf(value);}

function useProtectedImage(value:any,priority:Priority){
 const endpoint=endpointOf(value);const [version,setVersion]=useState(0);
 useEffect(()=>{
  if(!endpoint)return;const entry=entryFor(endpoint,priority);
  const notify=()=>setVersion(current=>current+1);entry.listeners.add(notify);schedule();
  return()=>{entry.listeners.delete(notify);};
 },[endpoint,priority]);
 void version;
 return endpoint?entries.get(endpoint)??entryFor(endpoint,priority):null;
}

export function ProtectedAssetImage({asset,alt,className,priority='high'}:{asset:any;alt:string;className?:string;priority?:Priority}){
 const entry=useProtectedImage(asset,priority);
 if(!entry?.src)return <span className={className} data-protected-image-state={entry?.state??'unavailable'} role={entry?.state==='error'?'status':undefined}>{entry?.state==='error'?'Иллюстрация не загрузилась':' '}</span>;
 return <img className={className} src={entry.src} alt={alt} decoding="async"/>;
}

/** Test-only observability; no endpoint identifiers are rendered to learners. */
export function protectedImageCacheStats(){return {entries:entries.size,active,maxConcurrent:MAX_CONCURRENT_IMAGE_FETCHES,states:[...entries.values()].map(entry=>({endpoint:entry.endpoint,state:entry.state,hasObjectUrl:Boolean(entry.src)}))};}
export async function waitForProtectedImages(){while([...entries.values()].some(entry=>entry.state==='idle'||entry.state==='loading')){schedule();await Promise.all([...entries.values()].map(entry=>entry.promise));}}
export function resetProtectedImageCacheForTest(){for(const entry of entries.values())if(entry.src)URL.revokeObjectURL(entry.src);entries.clear();active=0;}
